import React, { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from './App';
import ProtectedRoute from './components/ProtectedRoute';
import DevelopmentPhiGate from './components/common/DevelopmentPhiGate';
import OperationsFinanceLayout from './components/Layout/OperationsFinanceLayout';
import ReceiptsLayout from './components/Layout/ReceiptsLayout';
import ContractsLayout from './components/Layout/ContractsLayout';
import { SkeletonDashboard } from './components/common/Skeleton';

// Feature-based lazy loading
const authRoutes = {
  Login: React.lazy(() => import('./components/LandingPage/Login')),
  SessionExpired: React.lazy(() => import('./components/LandingPage/SessionExpired')),
  Unauthorized: React.lazy(() => import('./components/Unauthorized'))
};

const clientRoutes = {
  ClientProfile: React.lazy(() => import('./components/Clients/ClientProfile')),
  ClientIncidentReport: React.lazy(() => import('./pages/ClientIncidentReport')),
  AddClient: React.lazy(() => import('./components/Clients/AddClient')),
  ClientList: React.lazy(() => import('./components/Clients/ClientList'))
};

const adminRoutes = {
  AdminDashboard: React.lazy(() => import('./components/Admin/AdminDashboard')),
  AccessLogs: React.lazy(() => import('./components/Admin/AccessLogs')),
  TrainingsAdminPage: React.lazy(() => import('./pages/TrainingsAdminPage')),
  EmployeeReviewsAdminPage: React.lazy(() => import('./pages/EmployeeReviewsAdminPage')),
  EmailNotificationsAdminPage: React.lazy(() => import('./pages/EmailNotificationsAdminPage'))
};

const programRoutes = {
  AwpPage: React.lazy(() => import('./components/Programs/AwpPage')),
  AwpReportsPage: React.lazy(() => import('./components/Programs/AwpReportsPage')),
  DropInPage: React.lazy(() => import('./components/Programs/DropInComponent')),
  DropInReportsPage: React.lazy(() => import('./components/Programs/DropInReportsPage')),
  CoronadoReportsPage: React.lazy(() => import('./components/Programs/HousingReportsPage').then(m => ({ default: m.CoronadoReportsPage }))),
  EddyReportsPage: React.lazy(() => import('./components/Programs/HousingReportsPage').then(m => ({ default: m.EddyReportsPage }))),
  AWPBeds: React.lazy(() => import('./components/Programs/AWPBeds')),
  CoronadoHousing: React.lazy(() => import('./components/Programs/CoronadoHousing')),
  CoronadoRoomHistory: React.lazy(() => import('./components/Programs/CoronadoRoomHistory')),
  EddyHousing: React.lazy(() => import('./components/Programs/EddyHousing')),
  EddyRoomHistory: React.lazy(() => import('./components/Programs/EddyRoomHistory')),
  HousingRoomFacilitiesRequest: React.lazy(() => import('./pages/HousingRoomFacilitiesRequest')),
  AWomansPlace: React.lazy(() => import('./components/Programs/AWomansPlace')),
  MRSCPage: React.lazy(() => import('./components/Programs/MRSCPage')),
  WrcPage: React.lazy(() => import('./components/Programs/WrcPage')),
  WrcReportsPage: React.lazy(() => import('./components/Programs/WrcReportsPage')),
  MhopPage: React.lazy(() => import('./components/Programs/MhopPage')),
  SiteIncidentReport: React.lazy(() => import('./pages/SiteIncidentReport'))
};

const requestRoutes = {
  IncidentReports: React.lazy(() => import('./pages/IncidentReports')),
  ITRequests: React.lazy(() => import('./pages/ITRequests')),
  FacilitiesRequests: React.lazy(() => import('./pages/FacilitiesRequests')),
  PestControlForm: React.lazy(() => import('./pages/PestControlForm')),
  PurchasingRequests: React.lazy(() => import('./pages/PurchasingRequests')),
  DailyVehicleChecklist: React.lazy(() => import('./pages/DailyVehicleChecklist')),
  DriverInspectionEndOfShift: React.lazy(() => import('./pages/DriverInspectionEndOfShift'))
};

const otherRoutes = {
  LandingPage: React.lazy(() => import('./components/LandingPage/LandingPage')),
  GuestAwareHome: React.lazy(() => import('./components/LandingPage/GuestAwareHome')),
  ReportingPage: React.lazy(() => import('./components/Reporting/ReportingPage')),
  ProgramNotesInboxPage: React.lazy(() => import('./components/Programs/ProgramNotesInboxPage')),
  TableauDashboard: React.lazy(() => import('./components/Reporting/TableauDashboard')),
  ETHPage: React.lazy(() => import('./pages/ETHPage')),
  WomensFlexPage: React.lazy(() => import('./pages/WomensFlexPage')),
  PoliciesPage: React.lazy(() => import('./pages/Policies/PoliciesPage')),
  PolicyEditorPage: React.lazy(() => import('./pages/Policies/PolicyEditorPage')),
  CompleteRegistration: React.lazy(() => import('./components/Registration/CompleteRegistration')),
  TrainingsPage: React.lazy(() => import('./pages/TrainingsPage')),
  EmployeeReviewsPage: React.lazy(() => import('./pages/EmployeeReviewsPage')),
  ReliefPoolPage: React.lazy(() => import('./pages/ReliefPoolPage'))
};

const ReimbursementForm = React.lazy(() => import('./pages/ReimbursementForm'));
const CellphoneStipendForm = React.lazy(() => import('./pages/CellphoneStipendForm'));
const FinanceFormsPage = React.lazy(() => import('./pages/FinanceFormsPage'));

const receiptRoutes = {
  ReceiptsDashboardPage: React.lazy(() => import('./pages/receipts/ReceiptsDashboardPage')),
  ReceiptsUnsortedPage: React.lazy(() => import('./pages/receipts/ReceiptsUnsortedPage')),
  ReceiptsTransactionsPage: React.lazy(() => import('./pages/receipts/ReceiptsTransactionsPage')),
  ReceiptsTransactionDetailPage: React.lazy(() => import('./pages/receipts/ReceiptsTransactionDetailPage')),
  ReceiptsSettingsPage: React.lazy(() => import('./pages/receipts/ReceiptsSettingsPage')),
  ReceiptsImportPage: React.lazy(() => import('./pages/receipts/ReceiptsImportPage')),
  ReceiptsExportPage: React.lazy(() => import('./pages/receipts/ReceiptsExportPage'))
};

const contractRoutes = {
  ContractsListPage: React.lazy(() => import('./pages/contracts/ContractsListPage')),
  ContractsUnsortedPage: React.lazy(() => import('./pages/contracts/ContractsUnsortedPage')),
  ContractDetailPage: React.lazy(() => import('./pages/contracts/ContractDetailPage'))
};

const wrapReceiptsRoute = (PageComponent, { requireAdmin = false } = {}) => (
  <ProtectedRoute requireReceiptsAccess requireReceiptsAdmin={requireAdmin}>
    {withSuspense(
      <OperationsFinanceLayout>
        <ReceiptsLayout>
          <PageComponent />
        </ReceiptsLayout>
      </OperationsFinanceLayout>
    )}
  </ProtectedRoute>
);

const wrapContractsRoute = (PageComponent) => (
  <ProtectedRoute requireContractsAccess>
    {withSuspense(
      <OperationsFinanceLayout>
        <ContractsLayout>
          <PageComponent />
        </ContractsLayout>
      </OperationsFinanceLayout>
    )}
  </ProtectedRoute>
);
const NotFound = React.lazy(() => import('./components/NotFound'));

// Loading component
const LoadingFallback = () => <SkeletonDashboard />;

// Wrap components with Suspense
const withSuspense = Component => <Suspense fallback={<LoadingFallback />}>{Component}</Suspense>;

// Preload functions for frequently accessed routes
const preloadLogin = () => import('./components/LandingPage/Login');
const preloadClientList = () => import('./components/Clients/ClientList');
const preloadLandingPage = () => import('./components/LandingPage/LandingPage');
const preloadAddClient = () => import('./components/Clients/AddClient');
const preloadDropIn = () => import('./components/Programs/DropInComponent');
const preloadDropInReports = () => import('./components/Programs/DropInReportsPage');
const preloadCoronadoReports = () => import('./components/Programs/HousingReportsPage');
const preloadEddyReports = () => import('./components/Programs/HousingReportsPage');
const preloadWrcReports = () => import('./components/Programs/WrcReportsPage');
const preloadClientProfile = () => import('./components/Clients/ClientProfile');

// Preload on mouseover for better UX
const addPreloadOnHover = (preloadFn) => {
  if (typeof window !== 'undefined') {
    document.addEventListener('mouseover', (e) => {
      const link = e.target.closest('a[href]');
      if (link) {
        const href = link.getAttribute('href');
        // Trigger preload based on route
        if (href.includes('/add-client')) {
          preloadAddClient();
        } else if (href.includes('/drop-in/reports')) {
          preloadDropInReports();
        } else if (href.includes('/coronado/reports')) {
          preloadCoronadoReports();
        } else if (href.includes('/eddy/reports')) {
          preloadEddyReports();
        } else if (href.includes('/wrc/reports')) {
          preloadWrcReports();
        } else if (href.includes('/drop-in')) {
          preloadDropIn();
        } else if (href.includes('/client/')) {
          preloadClientProfile();
        } else if (href.includes('/client-list')) {
          preloadClientList();
        }
      }
    }, { passive: true });
  }
};

const withClientPhiGate = (node) => (
  <DevelopmentPhiGate>{node}</DevelopmentPhiGate>
);
if (typeof window !== 'undefined') {
  addPreloadOnHover();
}

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      children: [
        {
          path: 'login',
          element: withSuspense(<authRoutes.Login />),
          loader: preloadLogin
        },
        {
          path: 'session-expired',
          element: withSuspense(<authRoutes.SessionExpired />)
        },
        {
          index: true,
          element: withSuspense(
            <ProtectedRoute allowAuthenticatedGuest>
              <otherRoutes.GuestAwareHome />
            </ProtectedRoute>
          ),
          loader: preloadLandingPage
        },
        {
          path: 'add-client',
          element: withSuspense(
            <ProtectedRoute guestPermission="addClient" requireClientListAccess>
              {withClientPhiGate(<clientRoutes.AddClient />)}
            </ProtectedRoute>
          ),
          loader: preloadAddClient
        },
        {
          path: 'drop-in',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Drop-In']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.DropInPage />)}
            </ProtectedRoute>
          ),
          loader: preloadDropIn
        },
        {
          path: 'drop-in/reports',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Drop-In']} allowCompliance={true}>
              <programRoutes.DropInReportsPage />
            </ProtectedRoute>
          ),
          loader: preloadDropInReports
        },
        {
          path: 'drop-in/incident-report',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Drop-In']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.SiteIncidentReport />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'unauthorized',
          element: withSuspense(<authRoutes.Unauthorized />)
        },
        {
          path: 'client-list',
          element: withSuspense(
            <ProtectedRoute guestPermission="clientProfile" requireClientListAccess>
              {withClientPhiGate(<clientRoutes.ClientList />)}
            </ProtectedRoute>
          ),
          loader: preloadClientList
        },
        {
          path: 'client/:id',
          element: withSuspense(
            <ProtectedRoute guestPermission="clientProfile" requireClientListAccess>
              {withClientPhiGate(<clientRoutes.ClientProfile />)}
            </ProtectedRoute>
          ),
          loader: preloadClientProfile
        },
        {
          path: 'client/:id/incident-report',
          element: withSuspense(
            <ProtectedRoute guestPermission="clientProfile" requireClientListAccess>
              {withClientPhiGate(<clientRoutes.ClientIncidentReport />)}
            </ProtectedRoute>
          ),
          loader: preloadClientProfile
        },
        {
          path: 'admin-dashboard',
          element: withSuspense(<adminRoutes.AdminDashboard />)
        },
        {
          path: 'admin-settings',
          element: <Navigate to="/admin-dashboard" replace />
        },
        {
          path: 'admin/access-logs',
          element: withSuspense(<adminRoutes.AccessLogs />)
        },
        {
          path: 'admin/trainings',
          element: withSuspense(<adminRoutes.TrainingsAdminPage />)
        },
        {
          path: 'admin/employee-reviews',
          element: withSuspense(<adminRoutes.EmployeeReviewsAdminPage />)
        },
        {
          path: 'admin/email-notifications',
          element: withSuspense(<adminRoutes.EmailNotificationsAdminPage />)
        },
        {
          path: 'reports',
          element: withSuspense(
            <ProtectedRoute requireReportingAccess>
              <otherRoutes.ReportingPage />
            </ProtectedRoute>
          )
        },
        {
          path: 'program-notes',
          element: withSuspense(
            <ProtectedRoute>
              {withClientPhiGate(<otherRoutes.ProgramNotesInboxPage />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'tableau-dashboard',
          element: withSuspense(
            <ProtectedRoute requireReportingAccess>
              <otherRoutes.TableauDashboard />
            </ProtectedRoute>
          )
        },
        {
          path: 'coronado',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Coronado']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.CoronadoHousing />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'coronado/reports',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Coronado']} allowCompliance={true}>
              <programRoutes.CoronadoReportsPage />
            </ProtectedRoute>
          ),
          loader: preloadCoronadoReports
        },
        {
          path: 'coronado/rooms/:roomNumber',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Coronado']}>
              {withClientPhiGate(<programRoutes.CoronadoRoomHistory />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'coronado/rooms/:roomNumber/facilities-request',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Coronado']}>
              {withClientPhiGate(<programRoutes.HousingRoomFacilitiesRequest />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'coronado/incident-report',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Coronado']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.SiteIncidentReport />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'eddy',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Eddy']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.EddyHousing />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'eddy/reports',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Eddy']} allowCompliance={true}>
              <programRoutes.EddyReportsPage />
            </ProtectedRoute>
          ),
          loader: preloadEddyReports
        },
        {
          path: 'eddy/rooms/:roomNumber',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Eddy']}>
              {withClientPhiGate(<programRoutes.EddyRoomHistory />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'eddy/rooms/:roomNumber/facilities-request',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Eddy']}>
              {withClientPhiGate(<programRoutes.HousingRoomFacilitiesRequest />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'eddy/incident-report',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Eddy']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.SiteIncidentReport />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'eth',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['ETH']} guestPermission="programs">
              {withClientPhiGate(<otherRoutes.ETHPage />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'womens-flex',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Women\'s Flex']} guestPermission="programs">
              {withClientPhiGate(<otherRoutes.WomensFlexPage />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'awp',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['AWP']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.AwpPage />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'awp/reports',
          element: withSuspense(
            <ProtectedRoute requireReportingAccess>
              <programRoutes.AwpReportsPage />
            </ProtectedRoute>
          )
        },
        {
          path: 'awp/incident-report',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['AWP']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.SiteIncidentReport />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'awp-beds',
          element: withSuspense(withClientPhiGate(<programRoutes.AWPBeds />))
        },

        {
          path: 'awomansplace',
          element: withSuspense(withClientPhiGate(<programRoutes.AWomansPlace />))
        },
        {
          path: 'mrsc',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['MRSC']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.MRSCPage />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'mrsc/incident-report',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['MRSC']} guestPermission="programs">
              {withClientPhiGate(<programRoutes.SiteIncidentReport />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'wrc',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['WRC']} guestPermission="programs" requireLocationTag>
              {withClientPhiGate(<programRoutes.WrcPage />)}
            </ProtectedRoute>
          )
        },
        {
          path: 'wrc/reports',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['WRC']} allowCompliance={true}>
              <programRoutes.WrcReportsPage />
            </ProtectedRoute>
          ),
          loader: preloadWrcReports
        },
        {
          path: 'mhop',
          element: withSuspense(withClientPhiGate(<programRoutes.MhopPage />))
        },
        {
          path: 'behavioral-health',
          element: <Navigate to="/mhop" replace />
        },
        {
          path: 'incident-reports',
          element: withSuspense(withClientPhiGate(<requestRoutes.IncidentReports />))
        },
        {
          path: 'facilities-requests',
          element: withSuspense(<requestRoutes.FacilitiesRequests />)
        },
        {
          path: 'it-requests',
          element: withSuspense(<requestRoutes.ITRequests />)
        },
        {
          path: 'pest-control-form',
          element: withSuspense(
            <ProtectedRoute requiredLocations={['Coronado', 'Eddy']}>
              <requestRoutes.PestControlForm />
            </ProtectedRoute>
          )
        },
        {
          path: 'purchasing-requests',
          element: withSuspense(<requestRoutes.PurchasingRequests />)
        },
        {
          path: 'relief-pool',
          element: withSuspense(
            <ProtectedRoute>
              <otherRoutes.ReliefPoolPage />
            </ProtectedRoute>
          )
        },
        {
          path: 'daily-vehicle-checklist',
          element: withSuspense(<requestRoutes.DailyVehicleChecklist />)
        },
        {
          path: 'driver-inspection-end-of-shift',
          element: withSuspense(<requestRoutes.DriverInspectionEndOfShift />)
        },
        {
          path: 'policies',
          element: withSuspense(<otherRoutes.PoliciesPage />)
        },
        {
          path: 'policies/new',
          element: withSuspense(
            <ProtectedRoute>
              <otherRoutes.PolicyEditorPage mode="new" />
            </ProtectedRoute>
          )
        },
        {
          path: 'policies/:id/edit',
          element: withSuspense(
            <ProtectedRoute>
              <otherRoutes.PolicyEditorPage mode="edit" />
            </ProtectedRoute>
          )
        },
        {
          path: 'reimbursement-form',
          element: withSuspense(<ReimbursementForm />)
        },
        {
          path: 'cellphone-stipend-form',
          element: withSuspense(<CellphoneStipendForm />)
        },
        {
          path: 'vendor-forms',
          element: withSuspense(<FinanceFormsPage />)
        },
        {
          path: 'finance-forms',
          element: <Navigate to="/vendor-forms" replace />
        },

        {
          path: 'receipts',
          element: wrapReceiptsRoute(receiptRoutes.ReceiptsDashboardPage)
        },
        {
          path: 'receipts/unsorted',
          element: wrapReceiptsRoute(receiptRoutes.ReceiptsUnsortedPage)
        },
        {
          path: 'receipts/transactions',
          element: wrapReceiptsRoute(receiptRoutes.ReceiptsTransactionsPage)
        },
        {
          path: 'receipts/transactions/:id',
          element: wrapReceiptsRoute(receiptRoutes.ReceiptsTransactionDetailPage)
        },
        {
          path: 'receipts/settings',
          element: wrapReceiptsRoute(receiptRoutes.ReceiptsSettingsPage, { requireAdmin: true })
        },
        {
          path: 'receipts/import',
          element: wrapReceiptsRoute(receiptRoutes.ReceiptsImportPage)
        },
        {
          path: 'receipts/export',
          element: wrapReceiptsRoute(receiptRoutes.ReceiptsExportPage)
        },
        {
          path: 'contracts',
          element: wrapContractsRoute(contractRoutes.ContractsListPage)
        },
        {
          path: 'contracts/unsorted',
          element: wrapContractsRoute(contractRoutes.ContractsUnsortedPage)
        },
        {
          path: 'contracts/:id',
          element: wrapContractsRoute(contractRoutes.ContractDetailPage)
        },
        {
          path: 'trainings',
          element: withSuspense(<otherRoutes.TrainingsPage />)
        },
        {
          path: 'employee-reviews',
          element: withSuspense(<otherRoutes.EmployeeReviewsPage />)
        },
        {
          path: 'complete-registration',
          element: withSuspense(<otherRoutes.CompleteRegistration />)
        },
        {
          path: '*',
          element: withSuspense(<NotFound />)
        }
      ]
    }
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);
